/**
 * `AggregationElectionState` — aggregation election flow state
 * machine per design memo PR #150 §2.
 *
 * Discriminated-union states + reducer (`applyEvent`) lifted from
 * informed-export pattern (PR #60). Linear forward progression with
 * universal `cancel` / `error` transitions reachable from any
 * non-terminal state.
 *
 * **Phase A scaffolding** — UX shell + state machine + honest copy +
 * i18n + API client extensions. Substrate-event-triggered cadence
 * option (`substrate-event-triggered` cadence kind) gated on
 * cohort-flag from DEV6 surface per ADR-030 + BMA v4.0 §3.5.2 phased
 * rollout.
 *
 * **Doctrinal posture preserved at every state** per design memo §1:
 *   - ADR-026 §3 non-delegable fiduciary authority (election is
 *     patient-facing-account-control)
 *   - Patient-rights authority frame (NOT Patient-Directed Use
 *     Authority + Consent Artifact); aggregation election does NOT
 *     issue Consent Artifacts (memo §1.2)
 *   - Pattern B aggregation-payload posture (Issue #51); pipeline
 *     side already shipped via PR #99 + PR #123
 *   - Revocation ≠ erasure (ADR-024 §2.7.5); revocation surface
 *     surfaces honest "revoke-future-aggregation ≠ erase-prior-content"
 *     framing
 */
export const AGGREGATION_TERMINAL_STATES = new Set(['election-complete', 'cancelled', 'error']);
export function isTerminal(state) {
    return AGGREGATION_TERMINAL_STATES.has(state.kind);
}
// ---------- Transitions ----------
export class AggregationTransitionError extends Error {
    constructor(currentKind, eventKind) {
        super(`invalid aggregation transition: cannot apply event "${eventKind}" in state "${currentKind}"`);
        this.name = 'AggregationTransitionError';
    }
}
export function applyEvent(state, event) {
    if (!isTerminal(state)) {
        if (event.kind === 'cancel') {
            return { kind: 'cancelled', reason: event.reason };
        }
        if (event.kind === 'error') {
            return { kind: 'error', code: event.code, message: event.message };
        }
    }
    switch (state.kind) {
        case 'intro':
            if (event.kind === 'begin-inventory-load') {
                // Stays in intro until inventory loads; in-flight indicator
                // is the parent view's responsibility (loading spinner during
                // async inventory fetch).
                return state;
            }
            if (event.kind === 'inventory-loaded') {
                return { kind: 'holder-inventory', inventory: event.inventory };
            }
            break;
        case 'holder-inventory':
            if (event.kind === 'select-holders') {
                return {
                    kind: 'holder-selection',
                    inventory: state.inventory,
                    selectedHolderIds: event.holderIds,
                };
            }
            break;
        case 'holder-selection':
            if (event.kind === 'select-holders') {
                return { ...state, selectedHolderIds: event.holderIds };
            }
            if (event.kind === 'begin-per-holder-configuration') {
                if (state.selectedHolderIds.length === 0) {
                    return state;
                }
                return {
                    kind: 'per-holder-configuration',
                    inventory: state.inventory,
                    configs: [],
                    currentHolderIndex: 0,
                };
            }
            break;
        case 'per-holder-configuration':
            if (event.kind === 'configure-holder') {
                const next = {
                    holderId: event.holderId,
                    dataCategories: event.dataCategories,
                    cadence: event.cadence,
                    retention: event.retention,
                };
                const newConfigs = [...state.configs, next];
                const newIndex = state.currentHolderIndex + 1;
                return { ...state, configs: newConfigs, currentHolderIndex: newIndex };
            }
            if (event.kind === 'begin-confirmation') {
                return { kind: 'election-confirmation', configs: state.configs };
            }
            break;
        case 'election-confirmation':
            if (event.kind === 'confirm-election') {
                return { kind: 'election-executing', configs: state.configs };
            }
            break;
        case 'election-executing':
            if (event.kind === 'election-completed') {
                return {
                    kind: 'election-complete',
                    configs: state.configs,
                    initiatedFirstPullAt: event.initiatedFirstPullAt,
                };
            }
            break;
        default:
            break;
    }
    throw new AggregationTransitionError(state.kind, event.kind);
}
// ---------- Helpers ----------
export function allHoldersConfigured(state, selectedHolderIds) {
    return state.currentHolderIndex >= selectedHolderIds.length;
}
//# sourceMappingURL=state.js.map